import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getEmailActivityLogs, getEmailActivityStats, getEmailMarketingUsage, updateEmailMarketingUsageLimit } from "api/bMarketingApi";

export function useEmailActivityLogs(organisationId, filters = {}) {
  // Clean filters - convert "all" to empty string for API
  const cleanFilters = {
    ...filters,
    channel: filters.channel === "all" ? "" : filters.channel,
    event: filters.event === "all" ? "" : filters.event,
    location_id: filters.location_id === "all" ? "" : filters.location_id,
    is_test: filters.is_test === "all" ? "" : filters.is_test,
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["emailActivityLogs", organisationId, cleanFilters],
    queryFn: () => getEmailActivityLogs(organisationId, cleanFilters).then((res) => res.data),
    enabled: !!organisationId,
  });

  return {
    logs: data?.results || [],
    pagination: {
      count: data?.count || 0,
      numPages: data?.num_pages || 1,
      currentPage: data?.current_page || 1,
      hasNext: data?.has_next || false,
      hasPrevious: data?.has_previous || false,
    },
    isLoading,
    error,
    refetch,
  };
}

export function useEmailActivityStats(organisationId, filters = {}, rangeDays = 30, options = {}) {
  const cleanFilters = {
    ...filters,
    channel: filters.channel === "all" ? "" : filters.channel,
    section: options.section || filters.section || "all",
    metric: options.metric || filters.metric || "",
    campaign_id: options.campaignId || filters.campaign_id || "",
  };

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["emailActivityStats", organisationId, cleanFilters, rangeDays],
    queryFn: () => getEmailActivityStats(organisationId, rangeDays, cleanFilters).then((res) => res.data),
    enabled: !!organisationId && options.enabled !== false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    placeholderData: options.keepPreviousData
      ? (previousData) => previousData
      : undefined,
  });

  return {
    stats: data || {
      sent: 0,
      delivered: 0,
      opened: 0,
      clicked: 0,
      bounced: 0,
      spam_reported: 0,
      unsubscribed: 0,
      delivery_rate: 0,
      open_rate: 0,
      click_rate: 0,
      bounce_rate: 0,
      metrics: {},
      campaign_comparison: [],
      funnel: { aggregate: null, campaigns: [] },
    },
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

export function useEmailMarketingUsage(organisationId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["emailMarketingUsage", organisationId],
    queryFn: () => getEmailMarketingUsage(organisationId).then((res) => res.data),
    enabled: !!organisationId,
    refetchInterval: 30000,
  });

  return {
    usage: data || null,
    isLoading,
    error,
    refetch,
  };
}

export function useUpdateEmailMarketingUsageLimit(organisationId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (usageLimit) => updateEmailMarketingUsageLimit(organisationId, usageLimit).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emailMarketingUsage", organisationId] });
    },
  });
}
